import { useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFieldArray, useForm } from "react-hook-form";
import { ChevronDown, Plus } from "lucide-react";
import { EvaluationService, TestSuiteWithStats } from "@/generated";
import BaseDialog from "@/components/Modals/BaseDialog";
import { Button } from "@/components/shadcn/button";
import { Skeleton } from "@/components/shadcn/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import { NoxusInputField } from "@/components/Form/InputField";
import { SpotSelectField } from "@/components/Form/SelectField";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { SpotToast } from "@/components/SpotToast";
import { MAX_ENTITY_NAME } from "@/constants/entityLimits";
import { cn } from "@/lib/utils";
import { extractErrorMessage } from "@/utils/plugins";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { CheckDrawer } from "./CheckDrawer";
import { CheckContext } from "./CheckFields";
import { CheckRow } from "./CheckRow";
import { ContextTurn, ContextTurns } from "./ContextTurns";
import { AgentTestsT, CheckDraft, generatedName } from "./checkCatalog";
import { lastSet, rememberSet } from "./lastSet";
import { useCheckContext } from "./useCheckContext";
import { testPath } from "./paths";

const NEW_TEST_SET = "new";

type ReplyTestForm = {
  name: string;
  message: string;
  testSetId: string;
  checks: CheckDraft[];
};

export async function saveReplyTest({
  t,
  groupId,
  testSetId,
  conversationId,
  messageId,
  values,
}: {
  t: AgentTestsT;
  groupId: string;
  testSetId: string;
  conversationId: string;
  messageId: string;
  values: Pick<ReplyTestForm, "name" | "message" | "checks">;
}): Promise<string> {
  const { data } = await EvaluationService.createTestCaseFromMessage({
    path: {
      group_id: groupId,
      test_suite_id: testSetId,
      conversation_id: conversationId,
      message_id: messageId,
    },
    body: {
      name: values.name.trim(),
      message: values.message,
      evaluators: values.checks.map((check) => ({
        type: check.type,
        name: check.name || generatedName(t, check.type, check.expected),
        expected_output: check.expected,
      })),
    },
  });
  return data.id;
}

type DialogProps = {
  assistantId: string;
  conversationId: string;
  messageId: string;
  userMessage: string;
  context: ContextTurn[];
  onClose: () => void;
};

export function TestThisReplyDialog(props: DialogProps) {
  const t = useTranslations("agentTests");
  const { spotGroup } = useSpotUser();
  const { data: testSets, isError: isTestSetsError } = useRemoteData(
    DATA_KEYS.AGENT_TEST_SUITES,
    { path: { group_id: spotGroup.id, agent_id: props.assistantId } }
  );
  const { data: agent } = useRemoteData(DATA_KEYS.ASSISTANT_ID, {
    path: { group_id: spotGroup.id, assistant_id: props.assistantId },
  });
  const checkContext = useCheckContext(props.assistantId, true);

  if (isTestSetsError) {
    return (
      <Shell onClose={props.onClose}>
        <NoxusAlert
          variant="destructive"
          title={t("reply.loadFailedTitle")}
          description={t("closeAndRetry")}
        />
      </Shell>
    );
  }
  if (!testSets || !checkContext || !agent) {
    return (
      <Shell onClose={props.onClose}>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Shell>
    );
  }
  return (
    <ReplyTestFormDialog
      {...props}
      testSets={testSets}
      agentName={agent.name}
      checkContext={checkContext}
    />
  );
}

function Shell({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  const t = useTranslations("agentTests");
  return (
    <BaseDialog
      isOpen
      onOpenChange={(open) => !open && onClose()}
      title={t("reply.title")}
      size="2xl"
      footerActions={[
        { label: t("cancel"), props: { variant: "outline", onClick: onClose } },
      ]}
    >
      {children}
    </BaseDialog>
  );
}

function ReplyTestFormDialog({
  assistantId,
  conversationId,
  messageId,
  userMessage,
  context,
  onClose,
  testSets,
  agentName,
  checkContext,
}: DialogProps & {
  testSets: TestSuiteWithStats[];
  agentName: string;
  checkContext: CheckContext;
}) {
  const t = useTranslations("agentTests");
  const { spotGroup } = useSpotUser();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [checkDialog, setCheckDialog] = useState<{
    index: number | null;
  } | null>(null);
  const newSetName = t("reply.newSetName", { agent: agentName });

  const form = useForm<ReplyTestForm>({
    defaultValues: {
      name: userMessage.trim().slice(0, MAX_ENTITY_NAME),
      message: userMessage,
      testSetId:
        testSets.find((set) => set.id === lastSet(assistantId))?.id ??
        testSets[0]?.id ??
        NEW_TEST_SET,
      checks: [],
    },
  });
  const checks = useFieldArray({ control: form.control, name: "checks" });
  const checkValues = form.watch("checks");
  const editingIndex = checkDialog?.index ?? null;

  const saveMutation = useMutation({
    mutationFn: async (values: ReplyTestForm) => {
      const testSetId =
        values.testSetId === NEW_TEST_SET
          ? (
              await EvaluationService.createAgentTestSuite({
                path: { group_id: spotGroup.id, agent_id: assistantId },
                body: { name: newSetName, description: null },
              })
            ).data.id
          : values.testSetId;
      const testId = await saveReplyTest({
        t,
        groupId: spotGroup.id,
        testSetId,
        conversationId,
        messageId,
        values,
      });
      return { testSetId, testId };
    },
    onSuccess: async ({ testSetId, testId }) => {
      rememberSet(assistantId, testSetId);
      await Promise.all(
        [DATA_KEYS.TEST_CASES, DATA_KEYS.AGENT_TEST_SUITES].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] })
        )
      );
      const setName =
        testSets.find((set) => set.id === testSetId)?.name ?? newSetName;
      SpotToast.success(
        {
          title: t("reply.saved"),
          description: t("reply.addedTo", { name: setName }),
        },
        {
          label: t("reply.open"),
          onClick: () =>
            router.push(
              `/assistant/${assistantId}${testPath(testSetId, testId)}`
            ),
        },
        10_000
      );
      onClose();
    },
    onError: (error) =>
      SpotToast.error(extractErrorMessage(error) || t("test.saveFailed")),
  });
  const isSaving = saveMutation.isPending;
  const save = form.handleSubmit((values) => saveMutation.mutate(values));

  return (
    <BaseDialog
      isOpen
      onOpenChange={(open) => !open && !isSaving && onClose()}
      title={t("reply.title")}
      description={t("reply.description")}
      size="2xl"
      anchor="top"
      footerActions={[
        {
          label: t("cancel"),
          props: { variant: "outline", onClick: onClose, disabled: isSaving },
        },
        {
          label: t("save"),
          props: { onClick: save, loading: isSaving, disabled: isSaving },
        },
      ]}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <fieldset disabled={isSaving} className="contents">
          {context.length > 0 && (
            <Collapsible open={isContextOpen} onOpenChange={setIsContextOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="reply-test-context"
                  className="-ml-2"
                >
                  {t("reply.context")}
                  <span className="text-muted-foreground font-normal">
                    {t("reply.contextCount", { count: context.length })}
                  </span>
                  <ChevronDown
                    className={cn(
                      "size-4 transition-transform",
                      isContextOpen && "rotate-180"
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <ContextTurns turns={context} />
              </CollapsibleContent>
            </Collapsible>
          )}

          <NoxusInputField
            name="message"
            label={t("reply.userMessage")}
            control={form.control}
            placeholder={t("test.messagePlaceholder")}
            isTextarea
            isAutoResize
            rules={{ validate: (value: string) => Boolean(value.trim()) }}
          />

          <div className="grid grid-cols-2 gap-4">
            <NoxusInputField
              name="name"
              label={t("test.nameLabel")}
              control={form.control}
              maxLength={MAX_ENTITY_NAME}
              rules={{ validate: (value: string) => Boolean(value.trim()) }}
            />
            <SpotSelectField
              name="testSetId"
              label={t("reply.testSet")}
              control={form.control}
              options={[
                ...testSets.map((set) => ({ label: set.name, value: set.id })),
                { label: t("page.newSet"), value: NEW_TEST_SET },
              ]}
              defaultValue={testSets[0]?.id ?? NEW_TEST_SET}
              isNotSearchable
            />
          </div>

          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-medium">{t("panel.title")}</p>
              <p className="text-sm text-muted-foreground">
                {t("reply.checksDescription")}
              </p>
            </div>
            {checks.fields.map((field, index) => (
              <CheckRow
                key={field.id}
                check={checkValues[index]}
                onEdit={() => setCheckDialog({ index })}
                onRemove={() => checks.remove(index)}
              />
            ))}
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCheckDialog({ index: null })}
              >
                <Plus className="size-4" />
                {t("panel.add")}
              </Button>
            </div>
          </div>
        </fieldset>
      </form>

      {checkDialog !== null && (
        <CheckDrawer
          onClose={() => setCheckDialog(null)}
          context={checkContext}
          initial={editingIndex === null ? null : checkValues[editingIndex]}
          onSave={(check) => {
            if (editingIndex === null) {
              checks.append(check);
            } else {
              checks.update(editingIndex, check);
            }
          }}
        />
      )}
    </BaseDialog>
  );
}
